import { MutationResult, useApolloClient } from '@apollo/client';
import { useAuthToken } from './useAuthToken';
import { useHistory, useLocation } from 'react-router-dom';
import { useHeaLoginMutation } from '@src/api/graphql-codegen';
import { useState } from 'react';
import { useAnalytics, Events } from '@hooks';
import { getOrCreateSessionId, getAndClearSessionUserId, setSessionUserId } from '../utils/sentryTrackingConfig';
import { beginLogout, endLogout, getLogoutEventsSent, setLogoutEventsSent, resetLogoutTracking } from '../utils/logoutTrackingGuard';
import { logger } from '../utils/logger';

type UseLoginMutationResponse = [(email: string, password: string) => void, () => void, MutationResult];
export const useHsaLoginMutation = (): UseLoginMutationResponse => {
  const { setHsaAuthToken, removeHsaAuthToken, setAuthPassword, removeAuthPassword, setParentFirstLogin } = useAuthToken();
  const client = useApolloClient();
  const history = useHistory();
  const location = useLocation();
  const { identify, trackEvent, segment } = useAnalytics();
  const [password, setPassword] = useState('');

  const [loginMutation, mutationResults] = useHeaLoginMutation({
    onCompleted: (data) => {
      if (data.heaLogin.token) {
        resetLogoutTracking();
        setHsaAuthToken(data.heaLogin.token);
        setAuthPassword(password)
        setParentFirstLogin("YES");
        if (data.heaLogin.user?.id) {
          segment?.identify(data.heaLogin.user?.id);
          const sessionId = getOrCreateSessionId();
          const timestamp = new Date().toISOString();
          const loginPayload = { loginType: 'Email', name: 'HEA Login', userId: data.heaLogin.user?.id, sessionId, timestamp };
          trackEvent(Events.actions.logIn, loginPayload);
          trackEvent(Events.actions.sessionStarted, loginPayload);
          setSessionUserId(data.heaLogin.user.id);
          identify(data.heaLogin.user?.id, {
            email: data.heaLogin.user.email,
          });
        }
        if (location.pathname === '/login' && history.length > 3 && location?.key && history.action !== 'POP') {
          history.goBack();
        } else {
          history.push('/studyplan');
        }
      }
    },
    onError: (error) => {
      logger.debug(`[Error]`, error);
    },
  });

  const login = (email: string, password: string) => {
    removeHsaAuthToken();
    setPassword(password);
    return loginMutation({
      variables: {
        input: {
          email,
          password
        }
      }
    });
  };

  const logout = async () => {
    if (!beginLogout()) return;
    const withTimeout = async (p: Promise<unknown>, ms = 1200) => {
      await Promise.race([p, new Promise<void>((resolve) => setTimeout(resolve, ms))]);
    };
    // Send session_ended and log_out once, then continue logout
    if (!getLogoutEventsSent()) {
      setLogoutEventsSent(true);
      const sessionId = getOrCreateSessionId();
      const userId = getAndClearSessionUserId();
      try {
        await withTimeout(trackEvent(Events.actions.sessionEnded, {
          userId: userId ?? undefined,
          sessionId,
          timestamp: new Date().toISOString(),
        }));
        await withTimeout(trackEvent(Events.actions.logOut));
        const segmentWithQueue = segment as { queue?: { flush?: () => Promise<unknown> } } | undefined;
        if (typeof segmentWithQueue?.queue?.flush === 'function') {
          await withTimeout(segmentWithQueue.queue.flush());
        } else {
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch (e) {
        logger.error('[HSA Login] Error tracking logout events:', e);
      }
    }
    try {
      await client.clearStore();
    } catch (e) {
      logger.error('[HSA Login] clearStore failed during logout:', e);
    }
    removeHsaAuthToken();
    removeAuthPassword();
    endLogout();
  };

  return [login, logout, mutationResults];
}
